<template>
  <div class="flex flex-col gap-5">
    <h2 class="text-2xl font-bold text-white">Weekly Burn Sources</h2>
    <div class="bg-white/5 border border-white/20 rounded-lg p-5 flex flex-col gap-4 flex-1">
      <!-- Period Picker (데스크톱: 상단) -->
      <div class="flex justify-end mobile:hidden">
        <div class="flex gap-1 bg-white/5 p-1 rounded-lg w-[312px]">
          <button
            v-for="period in periods"
            :key="period.value"
            :class="[
              'flex-1 py-1 px-1 rounded-lg text-lg font-bold transition',
              modelValue === period.value
                ? 'bg-point-color text-white'
                : 'text-white hover:bg-white/10',
            ]"
            @click="$emit('update:modelValue', period.value)"
          >
            {{ period.label }}
          </button>
        </div>
      </div>

      <!-- Chart Container -->
      <div v-if="data" class="flex-1 flex items-center justify-center">
        <highcharts class="w-full h-full" :options="chartOptions" />
      </div>

      <!-- Legend (동적) -->
      <div v-if="data" class="flex items-center justify-center gap-5 flex-wrap">
        <div
          v-for="category in data.categories"
          :key="category"
          class="flex items-center gap-1"
        >
          <span
            class="w-2 h-2 rounded-full"
            :style="{ backgroundColor: data.categoryColors[category] }"
          />
          <span class="text-base font-normal text-white">{{ category }}</span>
        </div>
      </div>

      <!-- Powered by -->
      <span class="text-base font-normal text-white/40">Powered by Xangle</span>

      <!-- Period Picker (모바일: 하단, 전체 너비) -->
      <div class="hidden mobile:flex gap-1 bg-white/5 p-1 rounded-lg w-full">
        <button
          v-for="period in periods"
          :key="period.value"
          :class="[
            'flex-1 py-1 px-1 rounded-lg text-lg font-bold transition',
            modelValue === period.value
              ? 'bg-point-color text-white'
              : 'text-white hover:bg-white/10',
          ]"
          @click="$emit('update:modelValue', period.value)"
        >
          {{ period.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBreakpoints } from "@vueuse/core";
import { formatContractedNumber } from "~/utils/numberFormat";
import type {
  WeeklyBurnSourcesView,
  WeeklyBurnPeriod,
} from "~/api/service/livewatch/weekly-burn-sources.service";

const props = defineProps<{
  data: WeeklyBurnSourcesView | undefined;
  modelValue: WeeklyBurnPeriod;
}>();

defineEmits<{
  "update:modelValue": [value: WeeklyBurnPeriod];
}>();

const { createCustomTooltip } = useChartTooltip();
const breakpoints = useBreakpoints({ mobile: 840 });
const isMobile = breakpoints.smaller("mobile");

const periods = [
  { label: "3M", value: "3M" as const },
  { label: "6M", value: "6M" as const },
  { label: "1Y", value: "1Y" as const },
  { label: "MAX", value: "MAX" as const },
];

// 주차 포맷 헬퍼 함수
const formatWeekRange = (startDate: Date, endDate: Date, label: string) => {
  const startMonth = startDate.toLocaleDateString("en-US", { month: "short" });
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const year = endDate.getFullYear();
  return `${label} (${startMonth} ${startDay}-${endDay}, ${year})`;
};

const chartOptions = computed(() => {
  if (!props.data) return {};

  const data = props.data;
  const categories = data.categories;

  // 카테고리별 series 데이터 생성
  const seriesData = categories.map((category) => ({
    name: category,
    data: data.weeks.map((week) => {
      const item = week.items.find((i) => i.name === category);
      return item ? item.burnValue : 0;
    }),
    color: data.categoryColors[category],
  }));

  return {
    chart: {
      type: "column",
      height: 380,
      backgroundColor: "transparent",
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: data.weeks.map((w) => w.label),
      labels: {
        style: { color: "#9CA3AF", fontSize: "12px", fontFamily: "Montserrat" },
        // x축 라벨을 최대 13개(모바일 5개)만 표시
        step: Math.max(1, Math.ceil(data.weeks.length / (isMobile.value ? 5 : 13))),
      },
      lineColor: "transparent",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        style: { color: "#9CA3AF", fontSize: "12px", fontFamily: "Montserrat" },
        formatter: function (this: any) {
          return formatContractedNumber(this.value, undefined, 2);
        },
      },
      gridLineColor: "rgba(255,255,255,0.1)",
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        borderRadius: 4,
        pointPadding: 0.05,
        groupPadding: 0.1,
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      outside: true,
      backgroundColor: "none",
      borderWidth: 0,
      shadow: false,
      style: { fontSize: "12px", fontFamily: "Montserrat" },
      formatter: function (this: any) {
        const weekIndex = this.points[0].point.index;
        const week = data.weeks[weekIndex];
        return createCustomTooltip(this, {
          containerClass: "w-[280px]",
          date: () =>
            `<p class="font-bold">${formatWeekRange(week.startDate, week.endDate, week.label)}</p>`,
          content: (points: any[]) => {
            return points
              .map(
                (point: any) =>
                  `<div class="flex w-full items-center justify-between">
                <p><span style="color:${point.color};">● </span>${point.series.name}</p>
                <div class="font-bold">${formatContractedNumber(point.y, undefined, 2)} NESO</div>
              </div>`
              )
              .join("");
          },
          footer: (points: any[]) => {
            const total = points.reduce(
              (acc: number, point: any) => acc + point.y,
              0
            );
            return `<div class="flex w-full items-center justify-between">
              <p>Total</p>
              <div class="font-bold">${formatContractedNumber(total, undefined, 2)} NESO</div>
            </div>`;
          },
        });
      },
    },
    series: seriesData,
  };
});
</script>
